

import React, { useState, useRef } from "react";
import phoneIcon from "../Assets/Contact/phoneIcon.svg";
import mailIcon from "../Assets/Contact/mailIcon.svg";
import locationIcon from "../Assets/Contact/locationIcon.svg";
import { contactUs } from "../api/User";
import ReCAPTCHA from "react-google-recaptcha";

import toast, { Toaster } from "react-hot-toast";


function Contact() {
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_mail: "",
    customer_message: "",
  });
  const [captchaToken, setCaptchaToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_name || !formData.customer_mail || !formData.customer_message) {
      toast.error("All fields are required.");
      return;
    }
    if (!validateEmail(formData.customer_mail)) {
      toast.error("Invalid email format.");
      return;
    }
    if (!captchaToken) {
      toast.error("Please verify the reCAPTCHA.");
      return;
    }

    setLoading(true);
    try {
      let res = await contactUs({ ...formData, captcha: captchaToken });
      if (res.success) {
        toast.success("Message Submitted Successfully!");
        setFormData({ customer_name: "", customer_mail: "", customer_message: "" });
        recaptchaRef.current.reset();
      } else {
        toast.error(res.message || "Something went wrong.");
      }
    } catch (error) {
      toast.error("Failed to submit the form. Try again later.");
      console.error("error:", error);
    }
    setLoading(false);
  };

  const companyInfo = [
    {
      logo: locationIcon,
      title: "Address",
      info: "Suite# 803, 7th floor, Level-08, Vasavi MPM Grand, Ameerpet, Hyderabad, Telangana 500073",
    },
    {
      logo: phoneIcon,
      title: "Phone",
      info: "040-40060754",
    },
    {
      logo: mailIcon,
      title: "Email",
      info: "info@niruthi.com",
    },
  ];

  return (
    <div className="h-full rounded-lg bg-primaryBgColor dark:bg-darkModePrimaryBgColor p-4 custom-scrollbar overflow-y-auto">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6 bg-cardBgColor dark:bg-darkModeSecondaryBgColor rounded-lg border border-gray-200 dark:border-white/10 shadow-sm p-4">
        <h1 className="text-xl font-extrabold text-blackPrimaryText dark:text-darkModeBlackPrimaryText tracking-widest uppercase">
          Contact Us
        </h1>
        <div className="text-[10px] font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText opacity-50 uppercase tracking-tighter">
          Niruthi Climate and Ecosystems
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-auto lg:h-[calc(100%-80px)]">
        {/* Company Info Section */}
        <div className="flex flex-col gap-4 justify-center bg-cardBgColor dark:bg-darkModeCardBgColor border border-gray-200 dark:border-white/10 rounded-lg p-8 shadow-sm">
          <div className="mb-6">
            <h2 className="text-2xl font-extrabold text-wsText dark:text-darkModeBlackPrimaryText">Our Information</h2>
            <p className="text-sm text-blackPrimaryText dark:text-darkModeBlackPrimaryText opacity-60">We are here to help you</p>
          </div>

          <div className="space-y-8">
            {companyInfo.map((el, i) => (
              <div key={i} className="flex gap-6 items-start group">
                <div className="flex-shrink-0 bg-white bg-opacity-30 dark:bg-black dark:bg-opacity-30 p-3 rounded-xl border border-white border-opacity-20 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                  <img src={el.logo} alt={el.title} className="w-6 h-6 dark:invert" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-blackPrimaryText dark:text-darkModeBlackPrimaryText uppercase tracking-wider mb-1">{el.title}</h3>
                  <p className="text-sm text-blackPrimaryText dark:text-darkModeBlackPrimaryText leading-relaxed opacity-80">{el.info}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form Section */}
        <div className="bg-cardBgColor dark:bg-darkModeCardBgColor border border-gray-200 dark:border-white/10 rounded-lg p-8 shadow-sm flex flex-col justify-center items-center">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-extrabold text-wsText dark:text-darkModeBlackPrimaryText">Get in Touch</h2>
            <p className="text-sm text-blackPrimaryText dark:text-darkModeBlackPrimaryText opacity-60">Reach out to us anytime</p>
          </div>

          <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4 max-w-md">
            <div className="space-y-4">
              <input
                placeholder="Company/Full Name"
                className="w-full bg-white bg-opacity-40 dark:bg-black dark:bg-opacity-40 border border-white border-opacity-40 rounded-lg p-3 text-blackPrimaryText dark:text-darkModeBlackPrimaryText placeholder:text-gray-500 placeholder:text-sm focus:bg-white focus:bg-opacity-60 dark:focus:bg-opacity-60 transition-all outline-none border-wsText dark:border-wsText border-opacity-20 shadow-inner"
                type="text"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                required
              />
              <input
                placeholder="Your email"
                className="w-full bg-white bg-opacity-40 dark:bg-black dark:bg-opacity-40 border border-white border-opacity-40 rounded-lg p-3 text-blackPrimaryText dark:text-darkModeBlackPrimaryText placeholder:text-gray-500 placeholder:text-sm focus:bg-white focus:bg-opacity-60 dark:focus:bg-opacity-60 transition-all outline-none border-wsText dark:border-wsText border-opacity-20 shadow-inner"
                type="email"
                name="customer_mail"
                value={formData.customer_mail}
                onChange={handleChange}
                required
              />
              <textarea
                placeholder="How can we help you?"
                className="w-full bg-white bg-opacity-40 dark:bg-black dark:bg-opacity-40 border border-white border-opacity-40 rounded-lg p-3 text-blackPrimaryText dark:text-darkModeBlackPrimaryText placeholder:text-gray-500 placeholder:text-sm h-32 resize-none focus:bg-white focus:bg-opacity-60 dark:focus:bg-opacity-60 transition-all outline-none border-wsText dark:border-wsText border-opacity-20 shadow-inner"
                name="customer_message"
                value={formData.customer_message}
                onChange={handleChange}
                required
              ></textarea>
            </div>

            <div className="flex justify-center py-2">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_SITEKEY}
                onChange={handleCaptchaChange}
                theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
              />
            </div>

            <button
              type="submit"
              className="w-full bg-wsText hover:bg-opacity-95 text-white py-3 rounded-lg font-extrabold shadow-lg hover:shadow-xl transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-widest text-md"
              disabled={loading}
            >
              {loading ? "Sending..." : "Submit Message"}
            </button>
          </form>
        </div>
      </div>
      <Toaster />
    </div>
  );
}

export default Contact;

